import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/account/account.scss';
import '../../assets/styles/account/inactiveSocial.scss';
import usePageMeta from '../../services/page-meta';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value) {
    return (value || '').replace(/[()\s]+/g, '');
}

export default function InactiveSocialPage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.inactiveSocial.title', t('app.title')),
        description: t('meta.inactiveSocial.description', t('app.description')),
        keywords: t('meta.inactiveSocial.keywords', t('app.keywords')),
    });

    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [email, setEmail] = useState('');
    const [speciality, setSpeciality] = useState('');
    const [personalData, setPersonalData] = useState(true);
    const [showSubmitError, setShowSubmitError] = useState(false);
    const [errors, setErrors] = useState({});
    const [isEmailFocused, setIsEmailFocused] = useState(false);
    const [isSpecialityFocused, setIsSpecialityFocused] = useState(false);

    const showEmailField = !!payload?.showEmailField;
    const specialityCaption = useMemo(
        () => t('inactiveSocial.enterspecialtyCaption'),
        [t]
    );

    useEffect(() => {
        let isCancelled = false;

        fetch('/api/v1/inactiveSocial', {credentials: 'same-origin'})
            .then(async (response) => {
                let data = {};
                try {
                    data = await response.json();
                } catch (error) {
                    data = {};
                }

                if (!response.ok) {
                    throw data;
                }

                return data;
            })
            .then((data) => {
                if (isCancelled) {
                    return;
                }

                if (data?.state === 'redirect' && data?.redirectUrl) {
                    window.location.href = data.redirectUrl;
                    return;
                }

                setPayload(data);
            })
            .catch((errorPayload) => {
                if (isCancelled) {
                    return;
                }

                if (errorPayload?.redirectUrl) {
                    window.location.href = errorPayload.redirectUrl;
                    return;
                }

                setPayload({state: 'error'});
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    const validate = async () => {
        const nextErrors = {};
        const normalizedEmail = normalizeEmail(email);

        if (showEmailField) {
            if (!normalizedEmail || !EMAIL_REGEX.test(normalizedEmail)) {
                nextErrors.email = t('inactiveSocial.incorrectData');
            }
        }

        if (!speciality) {
            nextErrors.speciality = t('inactiveSocial.incorrectData');
        }

        if (!personalData) {
            nextErrors.personalData = true;
        }

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            return false;
        }

        if (showEmailField) {
            const checkAccountUrl = payload?.actions?.checkAccountUrl || '/api/v1/account/registration/checkAccount';
            const formData = new FormData();
            formData.append('email', normalizedEmail);

            const response = await fetch(checkAccountUrl, {
                method: 'POST',
                credentials: 'same-origin',
                body: formData,
            });
            const isEmailFree = await response.json();

            if (!isEmailFree) {
                setErrors({email: t('inactiveSocial.incorrectData')});
                return false;
            }
        }

        setErrors({});
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        setShowSubmitError(false);
        setIsSubmitting(true);

        try {
            const isValid = await validate();
            if (!isValid) {
                return;
            }

            const submitUrl = payload?.actions?.submitUrl || '/api/v1/profile/socialActivate';
            const formData = new FormData();
            formData.append('speciality', speciality);

            if (showEmailField) {
                formData.append('email', normalizeEmail(email));
            }

            const response = await fetch(submitUrl, {
                method: 'POST',
                credentials: 'same-origin',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('submit_failed');
            }

            window.location.href = '/';
        } catch (error) {
            setShowSubmitError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <section className="section flex-center" />;
    }

    if (payload?.state !== 'active') {
        return <section className="section flex-center" />;
    }

    return (
        <section className="section flex-center">
            <div className="authentication-form registration-form inactive-social-form">
                <h1>{showEmailField ? t('inactiveSocial.enterEmailCaption') : specialityCaption}</h1>
                <form className="form" action="#" method="post" id="social-inactive-email" onSubmit={handleSubmit}>
                    {showEmailField && (
                        <div className="form-group block-static-height js-form-group">
                            <label
                                htmlFor="emailRegistrationSocials"
                                className={`form-label${isEmailFocused || email ? ' active' : ''}`}
                            >
                                {t('inactiveSocial.email')}
                            </label>
                            <input
                                type="text"
                                name="email"
                                id="emailRegistrationSocials"
                                autoComplete="off"
                                className="form-input"
                                value={email}
                                onFocus={() => setIsEmailFocused(true)}
                                onBlur={() => setIsEmailFocused(false)}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                            <span className="info-text">{t('inactiveSocial.dataForLogin')}</span>
                            <span className={`error_text js-error${errors.email ? '' : ' hidden'}`}>
                                {errors.email || ''}
                            </span>
                        </div>
                    )}
                    <div className="form-group block-static-height select-block js-form-group">
                        <select
                            id="speciality"
                            name="speciality"
                            className="form-select"
                            value={speciality}
                            onFocus={() => setIsSpecialityFocused(true)}
                            onBlur={() => setIsSpecialityFocused(false)}
                            onChange={(event) => setSpeciality(event.target.value)}
                        >
                            <option value="" />
                            {(payload?.medicalSpecialities || []).map((medicalSpeciality) => (
                                <option value={medicalSpeciality.id} key={medicalSpeciality.id}>
                                    {medicalSpeciality.name}
                                </option>
                            ))}
                        </select>
                        <label
                            htmlFor="speciality"
                            className={`form-label select-label${isSpecialityFocused || speciality ? ' active' : ''}`}
                        >
                            {t('inactiveSocial.specialty')}
                        </label>
                        <span className={`error_text js-error${errors.speciality ? '' : ' hidden'}`}>
                            {errors.speciality || ''}
                        </span>
                    </div>
                    <div className="form-group check-group js-form-group">
                        <input
                            className="magic-checkbox"
                            type="checkbox"
                            name="personalData"
                            id="personalData"
                            checked={personalData}
                            onChange={(event) => setPersonalData(event.target.checked)}
                        />
                        <label
                            htmlFor="personalData"
                            dangerouslySetInnerHTML={{__html: t('inactiveSocial.termsAndConditions')}}
                        />
                        {errors.personalData && <span className="error-check" />}
                    </div>
                    {showSubmitError && (
                        <div className="error-block">
                            <div className="error_text">{t('inactiveSocial.incorrectData')}</div>
                        </div>
                    )}
                    <div className="submit-block">
                        <input
                            type="submit"
                            className={`btn js-submit-button${isSubmitting ? ' disabled' : ''}`}
                            value={t('inactiveSocial.submit')}
                            disabled={isSubmitting}
                        />
                    </div>
                </form>
            </div>
        </section>
    );
}
